import type { ApiKeyDTO } from '@atlas/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notification } from '@/notifications/notification-store'
import { apiKeyApi } from '@/services/api-key.service'

/**
 * The caller's own API keys.
 *
 * Mutations patch the cache in `onMutate` and restore the snapshot
 * unconditionally in `onError` — never branched on a status code, since a
 * rollback is correct for every failure — then invalidate in `onSettled`.
 * Mirrors `useTransitionWorkOrder`.
 *
 * Create is the exception: there is nothing to patch, because the row does not
 * exist until the server assigns its id and prefix. It only invalidates.
 */

const KEYS = ['api-keys'] as const

export const useApiKeys = () =>
	useQuery({
		queryKey: KEYS,
		queryFn: apiKeyApi.listApiKeys,
	})

/**
 * Create a key.
 *
 * Returns the created key INCLUDING its plaintext, which the caller must show
 * once and never store. It is deliberately not written into the query cache: a
 * cached plaintext would survive in memory long after the dialog closed, and
 * would be handed to every later reader of this key.
 */
export const useCreateApiKey = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: apiKeyApi.createApiKey,
		onError: (error: Error) => {
			notification.error(`Could not create the API key: ${error.message}`)
		},
		onSettled: () => qc.invalidateQueries({ queryKey: KEYS }),
	})
}

export const useRevokeApiKey = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (apiKeyId: string) => apiKeyApi.revokeApiKey(apiKeyId),
		onMutate: async (apiKeyId: string) => {
			await qc.cancelQueries({ queryKey: KEYS })
			const snapshot = qc.getQueryData<ApiKeyDTO[]>(KEYS)

			// Drop it from the list rather than restyling it: a revoked key is not
			// usable, and the server's own list is what decides how history is shown.
			qc.setQueryData<ApiKeyDTO[]>(KEYS, (keys) => keys?.filter((key) => key.apiKeyId !== apiKeyId))
			return { snapshot }
		},
		onError: (error: Error, _apiKeyId, ctx) => {
			qc.setQueryData(KEYS, ctx?.snapshot)
			notification.error(`Could not revoke the API key: ${error.message}`)
		},
		onSuccess: () => {
			notification.success('API key revoked')
		},
		onSettled: () => qc.invalidateQueries({ queryKey: KEYS }),
	})
}

interface RenameVars {
	apiKeyId: string
	name: string
}

export const useRenameApiKey = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ apiKeyId, name }: RenameVars) => apiKeyApi.renameApiKey(apiKeyId, { name }),
		onMutate: async ({ apiKeyId, name }: RenameVars) => {
			await qc.cancelQueries({ queryKey: KEYS })
			const snapshot = qc.getQueryData<ApiKeyDTO[]>(KEYS)

			qc.setQueryData<ApiKeyDTO[]>(KEYS, (keys) =>
				keys?.map((key) => (key.apiKeyId === apiKeyId ? { ...key, name } : key)),
			)
			return { snapshot }
		},
		onError: (error: Error, _vars, ctx) => {
			qc.setQueryData(KEYS, ctx?.snapshot)
			notification.error(`Could not rename the API key: ${error.message}`)
		},
		onSuccess: () => {
			notification.success('API key renamed')
		},
		onSettled: () => qc.invalidateQueries({ queryKey: KEYS }),
	})
}
